import { memo, type ComponentPropsWithoutRef } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { cn } from '@/lib/utils';
import { MathRenderer, extractMathBlocks } from './MathRenderer';

interface MathMarkdownProps {
  children: string | null | undefined;
}

// Cache per i blocchi estratti
const blockCache = new Map<
  string,
  Array<{ type: 'text' | 'math'; content: string; display: boolean }>
>();

// Function to filter out JSON content and replace with user-friendly message
function filterJournalContent(content: string): string {
  const startMarker = '[JOURNAL_JSON_START]';
  const endMarker = '[JOURNAL_JSON_END]';

  // Use a more robust approach to find the complete JSON block
  const startIndex = content.indexOf(startMarker);
  if (startIndex === -1) return content;

  const endIndex = content.indexOf(endMarker, startIndex + startMarker.length);
  if (endIndex === -1) return content;

  // Extract content before and after the JSON block
  const beforeJson = content.substring(0, startIndex).trim();
  const afterJson = content.substring(endIndex + endMarker.length).trim();

  // Build the replacement message
  let result = beforeJson;
  if (result && !result.endsWith('.') && !result.endsWith('!') && !result.endsWith('?')) {
    result += '.';
  }
  if (result) {
    result += '\n\n';
  }
  result += 'Diario completato.';
  if (afterJson) {
    result += `\n\n${  afterJson}`;
  }

  return result;
}

export const MathMarkdown = memo(({
  children,
}: MathMarkdownProps) => {
  const components: Components = {
    ol: ({ node: _node, children, ...rest }) => {
      const { className, ...listProps } = rest as ComponentPropsWithoutRef<'ol'>;
      return (
        <ol className={cn('list-decimal pl-5', className)} {...listProps}>
          {children}
        </ol>
      );
    },
    ul: ({ node: _node, children, ...rest }) => {
      const { className, ...listProps } = rest as ComponentPropsWithoutRef<'ul'>;
      return (
        <ul className={cn('list-disc pl-5', className)} {...listProps}>
          {children}
        </ul>
      );
    },
    li: ({ node: _node, children, ...rest }) => {
      const { className, ...itemProps } = rest as ComponentPropsWithoutRef<'li'>;
      return (
        <li className={cn('mb-2', className)} {...itemProps}>
          {children}
        </li>
      );
    },
    hr: () => <hr className="my-2" />,
    // Override per i paragrafi per gestire la matematica
    p: ({
      node: _node,
      children,
      ...props
    }: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any) => {
      if (typeof children === 'string') {
        return <MathParagraph content={children} />;
      }
      return (
        <p className="mb-2" {...props}>
          {children}
        </p>
      );
    },
  };

  // Filter content before rendering
  const filteredContent = children ? filterJournalContent(children) : children;

  return <ReactMarkdown components={components}>{filteredContent}</ReactMarkdown>;
});

interface MathParagraphProps {
  content: string;
}

const MathParagraph = memo(({
  content,
}: MathParagraphProps) => {
  // Filter content before processing
  const filteredContent = filterJournalContent(content);

  // Usa il cache per evitare re-estrazioni
  let blocks = blockCache.get(filteredContent);
  if (!blocks) {
    blocks = extractMathBlocks(filteredContent);
    blockCache.set(filteredContent, blocks);
  }

  return (
    <p className="mb-2">
      {blocks.map((block, index) => {
        if (block.type === 'math') {
          return (
            <MathRenderer
              key={`${filteredContent}-${index}`}
              content={block.content}
              display={block.display}
            />
          );
        } else {
          return <span key={`${filteredContent}-${index}`}>{block.content}</span>;
        }
      })}
    </p>
  );
});

// Componente per renderizzare testo con matematica senza wrapper di paragrafo
export const MathText = memo(({
  content,
}: {
  content: string;
}) => {
  // Filter content before processing
  const filteredContent = filterJournalContent(content);

  // Usa il cache per evitare re-estrazioni
  let blocks = blockCache.get(filteredContent);
  if (!blocks) {
    blocks = extractMathBlocks(filteredContent);
    blockCache.set(filteredContent, blocks);
  }

  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === 'math') {
          return (
            <MathRenderer
              key={`${filteredContent}-${index}`}
              content={block.content}
              display={block.display}
            />
          );
        } else {
          return <span key={`${filteredContent}-${index}`}>{block.content}</span>;
        }
      })}
    </>
  );
});
